import React, { useEffect, useState } from 'react';
import { GroupBase, MultiValue, Options, SingleValue, components } from 'react-select';
import { AsyncPaginate, LoadOptions } from 'react-select-async-paginate';

import { generatePlaceholder } from '@asd-stan/helpers/app-utils';
import { Tooltip } from '@components/tooltip/tooltip';
import { Flex } from '@components/utility/flex';
import { MarkedText } from '@components/utility/marked-text';
import { ErrorMessage, FieldProps } from 'formik';

import { StyledCheckboxOption, StyledFormSelect } from './form-select.styled';

import { StyledFieldErrorMessage } from '@components/utility/field-error-message.styled';

export interface Option {
	value: string;
	label: string | string[];
}

export type AdditionalType = {
	page: number;
};

const defaultAdditional: AdditionalType = {
	page: 1,
};

interface FormSelectAsyncProps {
	fullWidth?: boolean;
	title: string;
	mandatory?: boolean;
	tooltip?: string;
	disabled?: boolean;
	showError?: boolean;
	isMulti?: boolean;
	placeholder?: string;
	isLoading?: boolean;
	options?: Option[];
	defaultOptions?: Option[] | boolean;
	loadOptions: LoadOptions<Option, GroupBase<Option>, unknown>;
	onChange?: (name: string, option: Option | Option[]) => void;
	isOptionDisabled?: ((option: Option, selectValue: Options<Option>) => boolean) | undefined;
	showCheckboxOption?: boolean;
	closeMenuOnSelect?: boolean;
	hideSelectedOptions?: boolean;
	setTouchedOnBlur?: boolean;
	useSubmitOnChange?: boolean;
}

export const FormSelectAsyncPagination: React.FC<FormSelectAsyncProps & FieldProps> = ({
	form,
	field,
	fullWidth,
	title,
	mandatory,
	tooltip,
	disabled,
	showError,
	isMulti,
	placeholder,
	isLoading,
	options,
	defaultOptions,
	loadOptions,
	onChange,
	showCheckboxOption,
	closeMenuOnSelect = true,
	hideSelectedOptions = true,
	setTouchedOnBlur = false,
	useSubmitOnChange = false,
}) => {
	const [value, setValue] = useState<Option>(field.value);

	useEffect(() => {
		setValue(field.value);
	}, [field.value]);

	const handleChange = (option: MultiValue<Option> | SingleValue<Option>) => {
		if (onChange) {
			onChange(field.name, option as Option);
			setValue(prevState => field.value);
		} else {
			form.setFieldValue(field.name, option);
			setValue(field.value);
		}
	};

	const handleBlur = async () => {
		await form.setFieldTouched(field.name);
		if (useSubmitOnChange) {
			await form.handleSubmit();
		}
	};

	const CheckboxOption = (props: any) => {
		const option = props.options.find(({ value }: { value: any }) => value === props.value);
		return (
			<StyledCheckboxOption>
				<components.Option {...props}>
					<div className="container">
						{showCheckboxOption && (
							<input type="checkbox" checked={props.isSelected} onChange={() => null} />
						)}
						<label>{option.userLabel ? option.userLabel : option.label}</label>
						{props.data.roles && props.data.roles}
					</div>
				</components.Option>
			</StyledCheckboxOption>
		);
	};

	return (
		<StyledFormSelect
			$error={!!(form.touched[field.name] && form.errors[field.name])}
			$fullWidth={fullWidth}>
			<Flex>
				<label title={title} className={disabled ? 'disabled' : ''}>
					{title}
					{mandatory && <MarkedText>*</MarkedText>}
				</label>
				{tooltip ? <Tooltip text={tooltip} /> : null}
			</Flex>
			<AsyncPaginate
				isDisabled={disabled}
				classNamePrefix="Select"
				isMulti={isMulti}
				placeholder={generatePlaceholder(title, placeholder)}
				options={options}
				onBlur={handleBlur}
				onChange={handleChange}
				value={value}
				isLoading={isLoading}
				isClearable={!isMulti}
				components={{
					Option: CheckboxOption,
				}}
				defaultOptions={defaultOptions}
				loadOptions={loadOptions}
				additional={defaultAdditional}
				hideSelectedOptions={isMulti ? false : hideSelectedOptions && hideSelectedOptions}
				closeMenuOnSelect={
					form.values[field.name] && form.values[field.name].value === 'None'
						? false
						: isMulti
						  ? false
						  : closeMenuOnSelect && closeMenuOnSelect
				}
			/>
			{showError ? (
				<ErrorMessage
					name={field.name}
					children={(errorMessage: any) => {
						if (typeof errorMessage === 'string')
							return <StyledFieldErrorMessage>{errorMessage}</StyledFieldErrorMessage>;
						if (typeof errorMessage === 'object') {
							if (!isMulti) {
								return (
									<StyledFieldErrorMessage className="error-message">
										{Object.values(errorMessage)[0] as string}
									</StyledFieldErrorMessage>
								);
							} else {
								return (
									<StyledFieldErrorMessage className="error-message">
										{errorMessage[0].value}
									</StyledFieldErrorMessage>
								);
							}
						}
					}}
				/>
			) : null}
		</StyledFormSelect>
	);
};
